/**
 * Faction glyphs. BLUEPRINT section 3: BTC hex, ETH diamond, SOL bolt,
 * BNB hex cluster, XRP ring, plus a neutral node for colorless cards.
 *
 * The glyph is chosen by `faction`, which is card data, never by card name.
 */

import type { Faction } from '@/engine';

const GLYPH_PATHS: Record<Faction, string[]> = {
  btc: ['M12 2.6 20.1 7.3v9.4L12 21.4 3.9 16.7V7.3z', 'M9.6 8.2h4.1a2 2 0 0 1 0 4H9.6zm0 4h4.5a2 2 0 0 1 0 4H9.6z'],
  eth: ['M12 2.4 19 12l-7 9.6L5 12z', 'M5 12l7 3.4L19 12'],
  sol: ['M13.6 2.6 6.2 13.4h4.6L9.8 21.4 17.8 10h-4.9z'],
  bnb: ['M12 3 18.4 7.5v9L12 21l-6.4-4.5v-9z', 'M12 8.4 15.2 12 12 15.6 8.8 12z'],
  xrp: ['M12 3.4a8.6 8.6 0 1 0 0 17.2 8.6 8.6 0 0 0 0-17.2z', 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z'],
  neutral: ['M12 3.6 19.6 12 12 20.4 4.4 12z'],
};

export interface FactionGlyphProps {
  faction: Faction;
  size?: number;
  color?: string;
  className?: string;
  title?: string;
}

export function FactionGlyph({ faction, size = 14, color, className, title }: FactionGlyphProps) {
  const paths = GLYPH_PATHS[faction] ?? GLYPH_PATHS.neutral;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke={color ?? 'currentColor'}
      strokeWidth={1.8}
      strokeLinejoin="round"
      strokeLinecap="round"
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}

/** CSS custom property holding a faction's accent color. */
export function factionVar(faction: Faction): string {
  return `var(--${faction})`;
}

export function factionDimVar(faction: Faction): string {
  return `var(--${faction}-dim)`;
}
